import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicQueue } from "../../api";

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function BookingPage() {
  const { slug } = useParams();

  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [form, setForm] = useState({
    patient_name: "",
    patient_phone: "",
    reason_for_visit: "",
    insurance_type: "",
    queue_date: todayISO(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    publicQueue.kioskConfig(slug)
      .then(r => setConfig(r.data))
      .catch(() => setError("This facility could not be found."))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim() || !form.patient_phone.trim() || !form.queue_date) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await publicQueue.bookAppointment(slug, form);
      setResult(data);
    } catch (err) {
      setSubmitError(err.response?.data?.error || "Failed to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={S.center}><div style={S.loadingText}>Loading…</div></div>;
  }
  if (error) {
    return (
      <div style={S.center}>
        <div style={S.errorCard}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📡</div>
          <div style={{ color: "#EF4444", fontSize: "16px", fontWeight: "600" }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.root}>
      <div style={S.content}>
        <div style={S.header}>
          {config?.facility_logo && (
            <img src={config.facility_logo} alt={config.facility_name} style={S.facilityLogo} />
          )}
          <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="EliyaQ" style={S.brandLogo} />
        </div>
        <div style={S.facilityName}>{config?.facility_name || "Book an Appointment"}</div>
        <div style={S.subtitle}>Reserve your queue number in advance — no need to wait in line from the start.</div>

        {result ? (
          <div style={S.confirmCard}>
            <div style={{ fontSize: "44px", marginBottom: "10px" }}>✅</div>
            <div style={S.confirmTitle}>Appointment Booked</div>
            <div style={S.confirmTicket}>{result.ticket_number}</div>
            <div style={S.confirmDate}>{formatDate(result.queue_date)}</div>
            <div style={S.confirmDetail}>
              {result.station_name && <>First stop: <b>{result.station_name}</b><br /></>}
              We'll call this number in sequence on the day — please arrive in good time.
              {form.patient_phone && " A confirmation SMS has been sent."}
            </div>
            <button
              style={S.newBookingBtn}
              onClick={() => { setResult(null); setForm({ patient_name: "", patient_phone: "", reason_for_visit: "", insurance_type: "", queue_date: todayISO() }); }}
            >
              Book Another Appointment
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={S.form}>
            <Field label="Full Name *">
              <input
                style={S.input} value={form.patient_name} required
                onChange={e => set("patient_name", e.target.value)}
                placeholder="e.g. Kwame Mensah"
              />
            </Field>
            <Field label="Phone Number *" hint="We'll text you a confirmation and reminders">
              <input
                style={S.input} type="tel" required value={form.patient_phone}
                onChange={e => set("patient_phone", e.target.value)}
                placeholder="e.g. 024 123 4567"
              />
            </Field>
            <Field label="Reason for Visit" hint="Optional">
              <input
                style={S.input} value={form.reason_for_visit}
                onChange={e => set("reason_for_visit", e.target.value)}
                placeholder="e.g. General Checkup"
              />
            </Field>
            {config?.insurance_rules?.length > 0 ? (
              <Field label="Insurance / NHIS" hint="Sets your ticket-number prefix and where you're routed first">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "8px" }}>
                  {config.insurance_rules.map(rule => (
                    <button
                      key={rule.insurance_value}
                      type="button"
                      onClick={() => set("insurance_type", rule.insurance_value)}
                      style={{
                        padding: "12px 10px", borderRadius: "10px", cursor: "pointer",
                        border: `2px solid ${form.insurance_type === rule.insurance_value ? "#2563EB" : "#E2E8F0"}`,
                        background: form.insurance_type === rule.insurance_value ? "rgba(37,99,235,0.08)" : "#F8FAFC",
                        color: form.insurance_type === rule.insurance_value ? "#1D4ED8" : "#334155",
                        fontSize: "13px", fontWeight: "600", fontFamily: "inherit",
                      }}
                    >
                      {rule.display_label_en}
                    </button>
                  ))}
                </div>
              </Field>
            ) : (
              // No categories configured for this facility — free text is
              // purely informational then (nothing to match it against for
              // routing/prefix), same as before.
              <Field label="Insurance / NHIS" hint="Optional">
                <input
                  style={S.input} value={form.insurance_type}
                  onChange={e => set("insurance_type", e.target.value)}
                  placeholder="e.g. NHIS"
                />
              </Field>
            )}
            <Field label="Preferred Date *">
              <input
                style={S.input} type="date" required
                min={todayISO()} value={form.queue_date}
                onChange={e => set("queue_date", e.target.value)}
              />
            </Field>

            {submitError && <div style={S.errorMsg}>{submitError}</div>}

            <button type="submit" style={{ ...S.submitBtn, opacity: submitting ? 0.6 : 1 }} disabled={submitting}>
              {submitting ? "Booking…" : "Book Appointment"}
            </button>
          </form>
        )}

        <div style={S.footer}>Powered by EliyaQ</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={S.field}>
      <label style={S.label}>{label}</label>
      {children}
      {hint && <span style={S.hint}>{hint}</span>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

const S = {
  root: {
    minHeight: "100vh", background: "linear-gradient(160deg, #F0F6FF 0%, #E3EEFF 45%, #F5F9FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'DM Sans','Segoe UI',sans-serif", padding: "32px 16px",
  },
  center: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: "#F0F6FF", fontFamily: "'DM Sans','Segoe UI',sans-serif",
  },
  loadingText: { color: "#64748B", fontSize: "15px" },
  errorCard: { textAlign: "center" },
  content: { width: "100%", maxWidth: "480px" },
  header: { display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", marginBottom: "18px" },
  facilityLogo: { height: "48px", objectFit: "contain" },
  brandLogo: { height: "48px", width: "48px", borderRadius: "12px", objectFit: "cover" },
  facilityName: { fontSize: "24px", fontWeight: "800", color: "#0F1E3D", textAlign: "center", letterSpacing: "-0.3px" },
  subtitle: { fontSize: "14px", color: "#64748B", textAlign: "center", margin: "8px 0 28px", lineHeight: 1.5 },
  form: {
    background: "rgba(255,255,255,0.95)", border: "1px solid #E2E8F0", borderRadius: "20px",
    padding: "32px 28px", display: "flex", flexDirection: "column", gap: "18px",
    boxShadow: "0 20px 50px rgba(37,99,235,0.1)",
  },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: { fontSize: "13px", fontWeight: "700", color: "#334155" },
  hint: { fontSize: "11px", color: "#94A3B8" },
  input: {
    background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px",
    padding: "12px 14px", fontSize: "15px", color: "#0F1E3D", outline: "none",
    fontFamily: "inherit", width: "100%", boxSizing: "border-box",
  },
  errorMsg: { color: "#EF4444", fontSize: "13px", fontWeight: "600" },
  submitBtn: {
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)", color: "#fff", border: "none",
    borderRadius: "10px", padding: "14px", fontSize: "15px", fontWeight: "700", cursor: "pointer",
    marginTop: "4px", boxShadow: "0 10px 24px rgba(37,99,235,0.3)",
  },
  confirmCard: {
    background: "rgba(255,255,255,0.95)", border: "1px solid #E2E8F0", borderRadius: "20px",
    padding: "36px 28px", textAlign: "center", boxShadow: "0 20px 50px rgba(37,99,235,0.1)",
  },
  confirmTitle: { fontSize: "16px", fontWeight: "700", color: "#0F1E3D", marginBottom: "10px" },
  confirmTicket: { fontSize: "40px", fontWeight: "900", color: "#2563EB", letterSpacing: "-1px", marginBottom: "4px" },
  confirmDate: { fontSize: "14px", fontWeight: "600", color: "#D97706", marginBottom: "16px" },
  confirmDetail: { fontSize: "13px", color: "#64748B", lineHeight: 1.6, marginBottom: "22px" },
  newBookingBtn: {
    background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: "10px",
    padding: "12px 20px", fontSize: "14px", fontWeight: "600", color: "#2563EB",
    cursor: "pointer", fontFamily: "inherit",
  },
  footer: { textAlign: "center", fontSize: "12px", color: "#94A3B8", marginTop: "22px" },
};
